/**
 * ADR 0005 (butterfly check 2026-09-12, §2 E): Sentry is a log, and North Star principle 4 names logs.
 * One `beforeSend` for the API, the broker and the browser. It is an allowlist: an event leaves with the
 * fields named here and nothing else. Free text is the risk (Postgres `detail` carries row values, a
 * refusal message can carry a company's legal name, a breadcrumb carries a URL with a query string), so
 * exception values are reduced to type + code, messages survive only when the caller tagged them as a
 * code, request bodies / query strings / cookies / headers / extra / user are dropped, and breadcrumb
 * data is dropped with URLs cut at `?`. Pure: no SDK import, structural types only, so it is testable
 * without a DSN and shared by three runtimes.
 */

export interface ScrubbableFrame {
  filename?: string
  function?: string
  lineno?: number
  colno?: number
  in_app?: boolean
  [k: string]: unknown
}

export interface ScrubbableException {
  type?: string
  value?: string
  stacktrace?: { frames?: ScrubbableFrame[] }
  mechanism?: unknown
  [k: string]: unknown
}

export interface ScrubbableEvent {
  event_id?: string
  timestamp?: number | string
  platform?: string
  level?: string
  environment?: string
  release?: string
  dist?: string
  sdk?: unknown
  transaction?: string
  logger?: string
  server_name?: string
  message?: string | { formatted?: string, message?: string, params?: unknown[] }
  exception?: { values?: ScrubbableException[] }
  request?: { method?: string, url?: string, [k: string]: unknown }
  tags?: Record<string, unknown>
  breadcrumbs?: Array<{ type?: string, category?: string, level?: string, timestamp?: number, message?: string, data?: unknown, [k: string]: unknown }>
  contexts?: Record<string, unknown>
  [k: string]: unknown
}

/** Event-level keys that leave the process. Everything else (extra, user, modules, request bodies, …) is dropped. */
const EVENT_KEYS = new Set(['event_id', 'timestamp', 'platform', 'level', 'environment', 'release', 'dist', 'sdk', 'transaction', 'logger', 'server_name', 'type', 'start_timestamp', 'spans', 'measurements', 'transaction_info'])
/** Tag keys that are codes or ids, never names. `msg=code` marks a captureMessage whose text is a code. */
const TAG_KEYS = new Set(['path', 'route', 'fileId', 'rule', 'code', 'action', 'msg', 'app', 'runtime', 'transaction', 'url', 'http.method', 'http.status_code', 'handled', 'mechanism'])
/** Contexts that describe the machine, not the data. */
const CONTEXT_KEYS = new Set(['runtime', 'os', 'app', 'device', 'browser', 'trace', 'culture'])
const FRAME_KEYS = new Set(['filename', 'function', 'lineno', 'colno', 'in_app', 'module', 'abs_path', 'platform'])
const REDACTED = '[redacted]'

function cutQuery(url: string | undefined): string | undefined {
  if (typeof url !== 'string')
    return undefined
  const q = url.indexOf('?')
  return (q >= 0 ? url.slice(0, q) : url).replace(/#.*$/, '')
}

/** A code is short, has no spaces beyond single separators, no quotes, no `=`: a rule id, a PG SQLSTATE, an HTTP status. */
function isCode(s: unknown): s is string {
  return typeof s === 'string' && s.length <= 80 && /^[\w.:/-]+$/.test(s)
}

/** `PG_23505`, `rule:file.not_syncable`, `HTTP_502`: whatever the error carries that is a code, never its message. */
export function errorCode(err: unknown): string | null {
  if (err === null || typeof err !== 'object')
    return null
  const e = err as Record<string, unknown>
  if (isCode(e.code))
    return String(e.code)
  if (isCode(e.rule))
    return `rule:${e.rule}`
  if (typeof e.status === 'number')
    return `HTTP_${e.status}`
  if (isCode(e.name) && e.name !== 'Error')
    return String(e.name)
  return null
}

function scrubException(x: ScrubbableException, original: unknown): ScrubbableException {
  const type = typeof x.type === 'string' ? x.type : 'Error'
  const code = errorCode(original) ?? (isCode(x.value) ? x.value : null)
  const out: ScrubbableException = { type, value: code ? `${type} [${code}]` : type }
  if (x.stacktrace?.frames) {
    out.stacktrace = {
      frames: x.stacktrace.frames.map((f) => {
        const frame: ScrubbableFrame = {}
        for (const k of Object.keys(f)) {
          if (FRAME_KEYS.has(k))
            frame[k] = f[k]
        }
        // Source context lines can hold literal strings from the code; the line number is enough.
        return frame
      }),
    }
  }
  if (x.mechanism !== undefined)
    out.mechanism = x.mechanism
  return out
}

/**
 * Sentry `beforeSend`. `original` is `hint.originalException` when the SDK has it: its `code` / `rule` /
 * `status` become the exception value; its message never does. Returns a new event; never mutates.
 */
export function scrubSentryEvent<E extends object>(input: E, original?: unknown): E {
  // Structural: the SDKs' ErrorEvent / TransactionEvent types differ by runtime; the shape read here is common to all.
  const event = input as ScrubbableEvent
  const out: ScrubbableEvent = {}
  for (const k of Object.keys(event)) {
    if (EVENT_KEYS.has(k))
      out[k] = event[k]
  }
  if (event.exception?.values)
    out.exception = { values: event.exception.values.map(x => scrubException(x, original)) }
  if (event.message !== undefined) {
    const text = typeof event.message === 'string' ? event.message : event.message.formatted ?? event.message.message
    out.message = event.tags?.msg === 'code' && isCode(text) ? text : REDACTED
  }
  if (event.request) {
    const req: ScrubbableEvent['request'] = {}
    if (typeof event.request.method === 'string')
      req.method = event.request.method
    const url = cutQuery(event.request.url)
    if (url)
      req.url = url
    out.request = req
  }
  if (event.tags) {
    const tags: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(event.tags)) {
      if (TAG_KEYS.has(k) && (isCode(v) || typeof v === 'number' || typeof v === 'boolean'))
        tags[k] = k === 'url' ? cutQuery(String(v)) : v
    }
    out.tags = tags
  }
  if (event.breadcrumbs) {
    out.breadcrumbs = event.breadcrumbs.map((b) => {
      const crumb: NonNullable<ScrubbableEvent['breadcrumbs']>[number] = {}
      if (typeof b.type === 'string')
        crumb.type = b.type
      if (typeof b.category === 'string')
        crumb.category = b.category
      if (typeof b.level === 'string')
        crumb.level = b.level
      if (typeof b.timestamp === 'number')
        crumb.timestamp = b.timestamp
      // Navigation / fetch crumbs: keep the path, drop the query string, status codes and any body.
      const data = b.data as Record<string, unknown> | undefined
      if (data && (typeof data.url === 'string' || typeof data.to === 'string' || typeof data.from === 'string')) {
        const d: Record<string, unknown> = {}
        if (typeof data.method === 'string')
          d.method = data.method
        if (typeof data.status_code === 'number')
          d.status_code = data.status_code
        for (const k of ['url', 'to', 'from'] as const) {
          if (typeof data[k] === 'string')
            d[k] = cutQuery(data[k] as string)
        }
        crumb.data = d
      }
      if (b.category === 'console' || b.category === 'ui.click' || b.category === 'ui.input')
        crumb.message = REDACTED
      return crumb
    })
  }
  if (event.contexts) {
    const ctx: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(event.contexts)) {
      if (CONTEXT_KEYS.has(k))
        ctx[k] = v
    }
    out.contexts = ctx
  }
  return out as E
}

/** The option bag every runtime passes to `Sentry.init` (spread it; add the DSN and environment). */
export const SENTRY_SCRUB_OPTIONS = {
  sendDefaultPii: false,
  attachStacktrace: true,
  maxBreadcrumbs: 20,
  beforeSend: <E extends object>(event: E, hint?: { originalException?: unknown }) => scrubSentryEvent(event, hint?.originalException),
  beforeSendTransaction: <E extends object>(event: E) => scrubSentryEvent(event),
  beforeBreadcrumb: <B extends object>(crumb: B): B | null => {
    // Console lines and DOM events carry whatever was typed or logged; drop them at the source.
    const category = (crumb as { category?: string }).category
    if (category === 'console' || category === 'ui.input')
      return null
    return crumb
  },
} as const
